import Image from 'next/image'
import React, { useMemo, useState } from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'

import styles from './AdviserDetails.module.css'

type Avatar = {
  src: string
  alt: string
}

type Specialty = {
  title: string
  href: string
}

export interface AdviserDetailsProps {
  avatar: Avatar
  name: string

  /**
   * ?Not sure where this prop is used according to the design, so I'll apply it to the title attribute of the adviser's avatar.
   *
   * @author Trung<hou.dobaotrung@gmail.com>
   */
  title: string
  role: string
  link: string
  specialties: Specialty[]
  biography: React.ReactNode
}

export const AdviserDetails: React.FC<AdviserDetailsProps> = ({
  avatar,
  name,
  title,
  role,
  link,
  specialties,
  biography,
}) => {
  const [showMore, setShowMore] = useState(false)
  const maxWords = showMore ? Infinity : 69

  const isMaxWordsExceeded = useMemo(() => {
    if (typeof biography !== 'string') return false

    const words = biography.trim().split(' ')

    return words.length > maxWords
  }, [biography, maxWords])

  const renderBiography = () => {
    if (typeof biography === 'string') {
      // Trim the biography without cutting words. Max 100 words
      const words = biography.trim().split(' ')

      const shortenBiography = isMaxWordsExceeded
        ? words.slice(0, maxWords).join(' ')
        : words.join(' ')

      return shortenBiography
        .split('\n\n') // Split by paragraphs (double newline)
        .map((paragraph, index, arr) => (
          <p key={index}>
            {paragraph}
            {index === arr.length - 1 && isMaxWordsExceeded ? '...' : ''}
          </p>
        ))
    }
    return biography // Return as is if it's already JSX
  }

  // @ts-ignore
  const firstParagraph = biography?.[0]?.props?.blok?.content?.content?.[0]?.content?.[0]?.text
  let contentLength = 0
  if (biography && Array.isArray(biography) && biography[0]?.props?.blok?.content?.content) {
    contentLength = biography[0]?.props?.blok?.content?.content?.length || 0
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.avatarContainer}>
          <Image
            data-testid="avatar"
            title={title}
            width={93}
            height={93}
            src={avatar.src}
            alt={avatar.alt}
            unoptimized
            objectFit="contain"
            className={styles.avatar}
          />
          <div className="pl-3.5">
            <h2 className={styles.name}>{name}</h2>
            <p className={styles.role}>{role}</p>
          </div>
        </div>

        <div>
          <PhnxButton
            className={styles.viewBtn}
            href={link}
            type="link"
            label="View my profile"
            variant="darkest"
          />
        </div>
      </div>

      <div className={styles.specialties}>
        {specialties.map((specialty, index) => (
          <>
            <a key={index} href={specialty.href} className={styles.specialtyLink}>
              {specialty.title}
              {specialties.length > 1 && index < specialties.length - 1 && ', '}
            </a>
          </>
        ))}
      </div>

      <div className={styles.biography} data-testid="biography">
        {!showMore ? firstParagraph : renderBiography()}
      </div>

      {contentLength > 1 && (
        <button className={styles.readMore} onClick={() => setShowMore(!showMore)}>
          {showMore ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  )
}

export default AdviserDetails
